import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Resolver, useForm, useWatch, UseFormSetError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Globe, Sparkles, UserRound } from "lucide-react";
import { toast } from "sonner";
import { PageWrapper } from "@/components/PageWrapper";
import { Alert, Button } from "@/components/ui";
import { NotFoundPage } from "@/containers/NotFoundPage";
import { cn } from "@/lib/utils";
import { apiErrorMessage, isApiError } from "@/services/bedrock/apiError";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { AspectRatio, DesignPackageDetail, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { designEditorUrl, URLS } from "@/utils/routes";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { BriefPanel } from "./components/BriefPanel";
import { FormatAndSizeControls } from "./components/FormatAndSizeControls";
import { GLOBALS_STALE_TIME } from "./components/InspirationStrip";
import { ADMIN_NOTES_MAX, NOTES_MAX, NotesField } from "./components/NotesField";
import { PostKindPicker } from "./components/PostKindPicker";
import { StyleChips } from "./components/StyleChips";
import { composeBrief, structureSentence } from "./composeBrief";
import { DesignStudioHeader } from "./components/DesignStudioHeader";
import { WhatCanIAskDialog } from "./help/WhatCanIAskDialog";
import { useDesignPackageMutations } from "./hooks/useDesignPackageMutations";
import { DesignStudioScopeContext, DesignStudioScopeState } from "./DesignStudioScope";
import { chipsFor, deriveContextType, pruneChipIds, toggleChip } from "./styleChips";
import { TEMPLATE_PRESETS } from "./templatePresets";

export type DesignBasis = "review" | "other";

export interface NewDesignFormValues {
  basis: DesignBasis;
  format: "single" | "carousel";
  aspectRatio: AspectRatio | "auto";
  chipIds: string[];
  notes: string;
  referenceImageUrl?: string;
  templateId: number | null;
}

type PresetFields = Pick<NewDesignFormValues, "basis" | "format" | "aspectRatio" | "chipIds">;

const ASPECT_RATIOS = ["auto", "square_1_1", "portrait_4_5", "portrait_9_16"] as const;

export const newDesignSchema = (notesMax: number = NOTES_MAX) =>
  z
    .object({
      basis: z.enum(["review", "other"], { error: "Pick what this design is built around." }),
      format: z.enum(["single", "carousel"]),
      aspectRatio: z.enum(ASPECT_RATIOS),
      chipIds: z.array(z.string()),
      notes: z
        .string()
        .trim()
        .max(notesMax, `Keep notes under ${notesMax.toLocaleString("en-US")} characters.`),
      referenceImageUrl: z.string().url().optional(),
      templateId: z.number().nullable(),
    })
    .superRefine((values, ctx) => {
      const allowed = new Set(chipsFor(values.basis, values.format).map(chip => chip.id));
      if (values.chipIds.some(id => !allowed.has(id))) {
        ctx.addIssue({ code: "custom", path: ["chipIds"], message: "Some styles don't fit this kind of post." });
      }
    });

const REVIEW_DISABLED_MESSAGE = "Turn on Reputation Manager to design review posts";

export function newDesignDefaults(canCreateReviewDesigns: boolean): NewDesignFormValues {
  return {
    basis: canCreateReviewDesigns ? "review" : "other",
    format: "single",
    aspectRatio: "auto",
    chipIds: [],
    notes: "",
    referenceImageUrl: undefined,
    templateId: null,
  };
}

/** The template's preset, or its own structure with no chips (`about-player` for player templates). */
export function presetForTemplate(
  template: Pick<DesignPackageSummary, "name" | "contextType" | "isCarousel" | "aspectRatio">
): PresetFields {
  const preset = TEMPLATE_PRESETS[template.name];
  if (preset) {
    return { basis: preset.basis, format: preset.format, aspectRatio: preset.aspectRatio, chipIds: preset.chipIds };
  }
  return {
    basis: template.contextType === "review" ? "review" : "other",
    format: template.isCarousel ? "carousel" : "single",
    aspectRatio: template.aspectRatio,
    chipIds: template.contextType === "player" ? ["about-player"] : [],
  };
}

export const lastBriefKey = (packageId: number) => `designStudio.lastBrief.${packageId}`;

function readLastBrief(packageId: number): NewDesignFormValues | null {
  try {
    const raw = window.sessionStorage.getItem(lastBriefKey(packageId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NewDesignFormValues>;
    if (!parsed || typeof parsed !== "object" || !parsed.basis || !parsed.format || !parsed.aspectRatio) return null;
    return {
      basis: parsed.basis,
      format: parsed.format,
      aspectRatio: parsed.aspectRatio,
      chipIds: Array.isArray(parsed.chipIds) ? parsed.chipIds : [],
      notes: typeof parsed.notes === "string" ? parsed.notes : "",
      referenceImageUrl: parsed.referenceImageUrl || undefined,
      templateId: typeof parsed.templateId === "number" ? parsed.templateId : null,
    };
  } catch {
    return null;
  }
}

/** "Edit request" prefill: the saved form values, else the stored selections, else what the package itself says. */
export function editRequestValues(pkg: DesignPackageDetail): NewDesignFormValues {
  const saved = readLastBrief(pkg.id);
  if (saved) return saved;

  const structure = {
    basis: (pkg.contextType === "review" ? "review" : "other") as DesignBasis,
    format: (pkg.isCarousel ? "carousel" : "single") as NewDesignFormValues["format"],
    aspectRatio: pkg.requestedAspectRatio,
    referenceImageUrl: pkg.referenceImageUrl ?? undefined,
  };

  if (pkg.instructionsMeta) {
    return {
      ...structure,
      chipIds: pkg.instructionsMeta.chips,
      templateId: pkg.instructionsMeta.templateId,
      notes: "",
    };
  }
  return {
    ...structure,
    chipIds: pkg.contextType === "player" ? ["about-player"] : [],
    notes: pkg.instructions ?? "",
    templateId: null,
  };
}

type NewDesignLocationState = { templateId?: number | null } & Partial<NewDesignFormValues>;

function isFullPrefill(state: NewDesignLocationState | undefined): state is NewDesignFormValues {
  return !!state && typeof state === "object" && "basis" in state && "format" in state;
}

function samePreset(values: PresetFields, preset: PresetFields): boolean {
  return (
    values.basis === preset.basis &&
    values.format === preset.format &&
    values.aspectRatio === preset.aspectRatio &&
    values.chipIds.length === preset.chipIds.length &&
    values.chipIds.every(id => preset.chipIds.includes(id))
  );
}

function mapValidationErrors(error: unknown, setError: UseFormSetError<NewDesignFormValues>) {
  if (!isApiError(error, "validation_error")) return;
  const fields = (error.details?.fields ?? {}) as Record<string, string[] | string | undefined>;
  const first = (key: string) => {
    const value = fields[key];
    return Array.isArray(value) ? value[0] : value;
  };
  const notes = first("notes") ?? first("instructions");
  const basis = first("basis") ?? first("contextType");
  if (notes) setError("notes", { type: "server", message: notes });
  if (basis) setError("basis", { type: "server", message: basis });
}

/** One numbered step: a badge on a rail that connects to the next step, with the title and body beside it. */
const Step: React.FC<
  React.PropsWithChildren<{ step: 1 | 2 | 3 | 4; label: string; optional?: boolean; last?: boolean }>
> = ({ step, label, optional, last, children }) => {
  const titleId = `new-design-step-${step}`;
  return (
    <section aria-labelledby={titleId} className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-4">
      <div className="flex flex-col items-center">
        <span
          aria-hidden="true"
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary ring-1 ring-primary/20"
        >
          {step}
        </span>
        {!last && <span aria-hidden="true" className="mt-2 w-px flex-1 bg-slate-200" />}
      </div>
      <div className={cn("flex min-w-0 flex-col gap-4", !last && "pb-10")}>
        <h2 id={titleId} className="text-lg font-semibold leading-7 text-slate-800">
          {label}
          {optional && <span className="font-normal text-slate-400"> (optional)</span>}
        </h2>
        {children}
      </div>
    </section>
  );
};

interface NewDesignFormProps {
  canCreateReviewDesigns: boolean;
  adminScope: DesignStudioScopeState | undefined;
}

const NewDesignForm: React.FC<NewDesignFormProps> = ({ canCreateReviewDesigns, adminScope }) => {
  const history = useHistory();
  const location = useLocation<NewDesignLocationState | undefined>();
  const { generate } = useDesignPackageMutations();
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [showMobileBrief, setShowMobileBrief] = React.useState(false);

  // Location state is read once: the preset it names is applied on mount, then the form owns the values.
  const [initialState] = React.useState(() => location.state);
  const prefill = isFullPrefill(initialState) ? initialState : null;
  const pendingTemplateId = !prefill && initialState?.templateId != null ? initialState.templateId : null;

  const defaults = React.useMemo(() => newDesignDefaults(canCreateReviewDesigns), [canCreateReviewDesigns]);
  const notesMax = adminScope ? ADMIN_NOTES_MAX : NOTES_MAX;
  const schema = React.useMemo(() => {
    const base = newDesignSchema(notesMax);
    return canCreateReviewDesigns
      ? base
      : base.refine(values => values.basis !== "review", {
          path: ["basis"],
          message: REVIEW_DISABLED_MESSAGE,
        });
  }, [canCreateReviewDesigns, notesMax]);

  const form = useForm<NewDesignFormValues>({
    resolver: zodResolver(schema) as Resolver<NewDesignFormValues>,
    mode: "onTouched",
    defaultValues: prefill ? { ...defaults, ...prefill } : defaults,
  });
  const watched = useWatch({ control: form.control }) as NewDesignFormValues;
  const values: NewDesignFormValues = { ...defaults, ...watched, chipIds: watched.chipIds ?? [] };
  const { basis, format, aspectRatio, chipIds, notes } = values;

  const visibleChips = chipsFor(basis, format);
  const selectedChips = visibleChips.filter(chip => chipIds.includes(chip.id));
  const contextType = deriveContextType(basis, chipIds);
  const brief = composeBrief({ basis, contextType, format, aspectRatio, chips: selectedChips, notes });

  const appliedPreset = React.useRef<PresetFields | null>(prefill?.templateId != null ? prefill : null);

  React.useEffect(() => {
    const current = form.getValues("chipIds") ?? [];
    const pruned = pruneChipIds(current, basis, format);
    if (pruned.length !== current.length) form.setValue("chipIds", pruned);
  }, [basis, format, form]);

  const chipKey = chipIds.join(",");
  React.useEffect(() => {
    const preset = appliedPreset.current;
    if (!preset || form.getValues("templateId") == null) return;
    if (!samePreset({ basis, format, aspectRatio, chipIds: form.getValues("chipIds") ?? [] }, preset)) {
      appliedPreset.current = null;
      form.setValue("templateId", null);
    }
  }, [basis, format, aspectRatio, chipKey, form]);

  const applyTemplate = React.useCallback(
    (template: DesignPackageSummary) => {
      const preset = presetForTemplate(template);
      appliedPreset.current = preset;
      form.reset({ ...defaults, ...preset, templateId: template.id });
    },
    [defaults, form]
  );

  // "Start from this look" hands over only the template's id; its preset is applied once the detail loads.
  const { data: resolvedTemplate } = useQuery({
    queryKey: designKeys.detail(pendingTemplateId ?? 0),
    queryFn: () => DesignPackageServices.get(pendingTemplateId as number),
    enabled: pendingTemplateId != null,
    staleTime: GLOBALS_STALE_TIME,
  });
  const templateResolved = React.useRef(false);
  React.useEffect(() => {
    if (!resolvedTemplate || templateResolved.current) return;
    templateResolved.current = true;
    applyTemplate(resolvedTemplate);
  }, [resolvedTemplate, applyTemplate]);

  const submitting = form.formState.isSubmitting;
  const listUrl = adminScope ? URLS.admin.designStudio : URLS.coach.designStudio;

  const onSubmit = async (submitted: NewDesignFormValues) => {
    const submittedContextType = deriveContextType(submitted.basis, submitted.chipIds);
    const instructions = composeBrief({
      basis: submitted.basis,
      contextType: submittedContextType,
      format: submitted.format,
      aspectRatio: submitted.aspectRatio,
      chips: chipsFor(submitted.basis, submitted.format).filter(chip => submitted.chipIds.includes(chip.id)),
      notes: submitted.notes,
    });
    const scope =
      adminScope?.scope === "global"
        ? { isGlobal: true }
        : adminScope?.scope === "coach" && adminScope.coachId != null
        ? { coachId: adminScope.coachId }
        : {};

    try {
      const pkg = await generate.mutateAsync({
        instructions,
        contextType: submittedContextType,
        isCarousel: submitted.format === "carousel",
        aspectRatio: submitted.aspectRatio,
        referenceImageUrl: submitted.referenceImageUrl,
        chips: submitted.chipIds,
        templateId: submitted.templateId ?? undefined,
        ...scope,
      });
      try {
        window.sessionStorage.setItem(lastBriefKey(pkg.id), JSON.stringify(submitted));
      } catch {
        // Storage can be full or blocked; Edit request then falls back to the package's own fields.
      }
      history.replace(designEditorUrl(pkg.id, { admin: !!adminScope }));
    } catch (error) {
      mapValidationErrors(error, form.setError);
      toast.error(apiErrorMessage(error, "We couldn't start this design. Try again."));
    }
  };

  const briefPanel = (
    <BriefPanel
      basis={basis}
      contextType={contextType}
      format={format}
      aspectRatio={aspectRatio}
      brief={brief}
      onOpenHelp={() => setHelpOpen(true)}
    />
  );

  return (
    <PageWrapper hideSeparator fitViewport className="flex min-h-0 flex-1 flex-col gap-0">
      <DesignStudioHeader backTo={adminScope ? URLS.admin.designStudio : URLS.coach.designStudio}>
        <div className="text-xl leading-7 font-bold text-slate-800">New design</div>
      </DesignStudioHeader>
      {/* relative: visually hidden inputs are absolutely positioned; without it they escape this scroll
          area and stretch the whole page, which then scrolls too. */}
      <div className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-4 md:px-8 md:pt-6">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8 pb-8 md:pb-12">
          <form
            id="new-design-form"
            className="flex flex-col gap-8 min-w-0"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
          >
            {adminScope && (
              <Alert>
                {adminScope.scope === "global" ? <Globe aria-hidden="true" /> : <UserRound aria-hidden="true" />}
                <Alert.Description>
                  {adminScope.scope === "global" ? (
                    <p>
                      Creating a global design. Coaches will see it in{" "}
                      <strong className="font-semibold text-slate-700">Inspiration</strong> once it's published.
                    </p>
                  ) : (
                    `Creating for ${adminScope.coach?.label ?? ""}.`
                  )}
                </Alert.Description>
              </Alert>
            )}

            <div className="flex flex-col">
              <Step step={1} label="What's this design built around?">
                <PostKindPicker
                  value={basis}
                  onChange={next => form.setValue("basis", next, { shouldValidate: form.formState.isSubmitted })}
                  canCreateReviewDesigns={canCreateReviewDesigns}
                  disabled={submitting}
                />
                {form.formState.errors.basis?.message && (
                  <p className="text-sm text-destructive">{form.formState.errors.basis.message}</p>
                )}
              </Step>

              <Step step={2} label="Format and size">
                <FormatAndSizeControls
                  format={format}
                  aspectRatio={aspectRatio}
                  onFormatChange={next => form.setValue("format", next)}
                  onAspectRatioChange={next => form.setValue("aspectRatio", next)}
                  disabled={submitting}
                />
              </Step>

              <Step step={3} label="Style">
                <StyleChips
                  chips={visibleChips}
                  selectedIds={chipIds}
                  onToggle={chip => form.setValue("chipIds", toggleChip(form.getValues("chipIds") ?? [], chip))}
                  disabled={submitting}
                />
              </Step>

              <Step step={4} label="Notes" optional last>
                <NotesField form={form} max={notesMax} disabled={submitting} />
              </Step>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-6">
              <Button type="button" variant="ghost" disabled={submitting} onClick={() => history.push(listUrl)}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                <Sparkles aria-hidden="true" />
                Generate design
              </Button>
            </div>
          </form>

          <aside className="hidden lg:block">
            <div className="sticky top-4">{briefPanel}</div>
          </aside>
        </div>

        <div className="lg:hidden sticky bottom-0 -mx-4 border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-600">{structureSentence({ contextType, format, aspectRatio })}</span>
            <Button variant="link" size="inline" onClick={() => setShowMobileBrief(open => !open)}>
              {showMobileBrief ? "Hide brief" : "Show brief"}
            </Button>
          </div>
          {showMobileBrief && briefPanel}
        </div>
      </div>

      <WhatCanIAskDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </PageWrapper>
  );
};

export function NewDesignPage(): JSX.Element {
  const access = useDynamicPromptsAccess();
  const adminScope = React.useContext(DesignStudioScopeContext);
  const history = useHistory();
  // Only a typed URL reaches here in Coach scope without a coach; the list asks for one first.
  const needsCoach = adminScope?.scope === "coach" && adminScope.coachId == null;

  React.useEffect(() => {
    if (needsCoach) history.replace(URLS.admin.designStudio);
  }, [needsCoach, history]);

  if (access.loading) return <PageWrapper loading />;
  if (!access.canEdit) return <NotFoundPage />;
  if (needsCoach) return <></>;

  return <NewDesignForm canCreateReviewDesigns={access.canCreateReviewDesigns} adminScope={adminScope} />;
}
